import type React from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import * as d3 from 'd3';
import {Box, IconButton, Paper, Stack, Typography} from '@mui/material';
import {Add, CenterFocusStrong, Public, Remove} from '@mui/icons-material';
import {type Country, countryToPoint} from '~/components/visualization/bubble-map/countries';
import {loadWorldOutline} from '~/components/visualization/bubble-map/worldOutline';

/** Spend at a place: a country code, or any key with its own coordinates (e.g. a region) */
export type GeoSpendPoint = {key: string; spend: number; label?: string; lonLat?: [number, number] | null};

type Props = {
    data: GeoSpendPoint[];
    money: (v: number) => string;
    /** Height of the drawing in the 640 wide frame (default 300) */
    height?: number;
    /** Fill the height of the parent instead (the parent needs a height) */
    fill?: boolean;
    /** Highlighted bubbles */
    selected?: string[];
    /** When given, clicking a bubble toggles it in the selection */
    onSelectionChange?: (selected: string[]) => void;
    emptyText?: string;
};

const W = 640;
const LAND = '#ECEEF1';
const BORDER = '#D3D7DE';
export const GEO_SPEND_BUBBLE_COLOR = '#2D6FF5';
const BUBBLE = GEO_SPEND_BUBBLE_COLOR;

type Bubble = {key: string; label: string; spend: number; lonLat: [number, number]};

/** Selected bubbles stand out; without a selection all look the same */
function bubbleOpacity(key: string, selected: string[] | undefined): {fill: number; stroke: number} {
    if (!selected || selected.length === 0) return {fill: 0.35, stroke: 1};
    return selected.includes(key) ? {fill: 0.8, stroke: 1} : {fill: 0.12, stroke: 0.4};
}

/** A zoom factor, the whole world, or back to the opening frame on the spend countries. */
type ZoomAction = number | 'world' | 'home';
const BUTTONS: {label: string; Icon: typeof Add; action: ZoomAction}[] = [
    {label: 'Zoom in', Icon: Add, action: 1.6},
    {label: 'Zoom out', Icon: Remove, action: 1 / 1.6},
    {label: 'Show the whole world', Icon: Public, action: 'world'},
    {label: 'Back to the spend countries', Icon: CenterFocusStrong, action: 'home'},
];

/**
 * Geographic distribution (spec 4.4): spend by supplier country as bubbles on a light world map, framed on the
 * countries with spend. Scroll to zoom, drag to pan, or use the + / − buttons.
 * Used by the Category Profile and the Spend Landscape (where clicking a bubble filters on it).
 */
export const GeoSpendMap: React.FC<Props> = ({data, money, height = 300, fill, selected, onSelectionChange, emptyText}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const boxRef = useRef<HTMLDivElement>(null);
    // When filling the parent, the frame is as tall as the box allows at 640 wide
    const [fillHeight, setFillHeight] = useState<number | null>(null);
    useEffect(() => {
        const box = boxRef.current;
        if (!fill || !box) return undefined;
        const observer = new ResizeObserver(([entry]) => {
            const {width, height: boxHeight} = entry.contentRect;
            if (width > 0 && boxHeight > 0) setFillHeight(Math.round(W * boxHeight / width));
        });
        observer.observe(box);
        return () => observer.disconnect();
    }, [fill]);
    const H = fill && fillHeight ? fillHeight : height;
    const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
    const worldRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
    const [hover, setHover] = useState<{b: Bubble; x: number; y: number} | null>(null);
    // Read by the click handler, so a new selection does not redraw the map
    const selectionRef = useRef({selected, onSelectionChange});
    useEffect(() => {
        selectionRef.current = {selected, onSelectionChange};
    }, [selected, onSelectionChange]);
    const selectable = Boolean(onSelectionChange);
    const bubbles: Bubble[] = useMemo(() => data
        .filter(r => r.key && r.spend > 0)
        .map(r => ({key: r.key, label: r.label ?? r.key, spend: r.spend, lonLat: r.lonLat ?? countryToPoint(r.key as Country)}))
        .filter((b): b is Bubble => b.lonLat !== null), [data]);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg || bubbles.length === 0) return undefined;
        let cancelled = false;
        void loadWorldOutline().then(geo => {
            if (cancelled) return;
            const root = d3.select(svg);
            root.selectAll('*').remove();
            const view = root.append('g');

            // Frame the countries with spend (with some margin); a single country gets a regional view.
            const points: GeoJSON.Feature = {type: 'Feature', properties: {}, geometry: {
                type: 'MultiPoint', coordinates: bubbles.map(b => b.lonLat)}};
            // Close neighbours (NL, BE, DE) would zoom in too far, so cap the scale and re-centre on the countries.
            const projection = d3.geoMercator();
            const scale = bubbles.length > 1
                ? Math.min(projection.fitExtent([[W * 0.2, H * 0.2], [W * 0.8, H * 0.8]], points).scale(), 600)
                : 500;
            projection.scale(scale).center(d3.geoCentroid(points)).translate([W / 2, H / 2]);
            const path = d3.geoPath(projection);

            if (geo) {
                view.append('g').selectAll('path').data(geo.features).enter().append('path')
                    .attr('d', f => path(f) ?? '').attr('fill', LAND).attr('stroke', BORDER).attr('stroke-width', 0.5)
                    .attr('vector-effect', 'non-scaling-stroke');
            }
            const max = d3.max(bubbles, b => b.spend) ?? 1;
            const radius = d3.scaleSqrt().domain([0, max]).range([0, 22]);
            view.append('g').selectAll('circle').data([...bubbles].sort((a, b) => b.spend - a.spend)).enter().append('circle')
                .attr('cx', b => projection(b.lonLat)?.[0] ?? 0).attr('cy', b => projection(b.lonLat)?.[1] ?? 0)
                .attr('r', b => Math.max(4, radius(b.spend)))
                .attr('fill', BUBBLE).attr('fill-opacity', b => bubbleOpacity(b.key, selectionRef.current.selected).fill)
                .attr('stroke', BUBBLE).attr('stroke-opacity', b => bubbleOpacity(b.key, selectionRef.current.selected).stroke)
                .attr('stroke-width', 1.5)
                .attr('vector-effect', 'non-scaling-stroke').style('cursor', selectable ? 'pointer' : 'default')
                .on('click', (_e: MouseEvent, b) => {
                    const {selected: current = [], onSelectionChange: onChange} = selectionRef.current;
                    onChange?.(current.includes(b.key) ? current.filter(k => k !== b.key) : [...current, b.key]);
                })
                .on('mousemove', (e: MouseEvent, b) => {
                    const box = svg.getBoundingClientRect();
                    setHover({b, x: e.clientX - box.left, y: e.clientY - box.top});
                })
                .on('mouseleave', () => setHover(null));

            // Zoom out as far as the whole world (fitted to the frame) and keep the world in view when panning.
            const [[x0, y0], [x1, y1]] = geo ? path.bounds(geo) : [[0, 0], [W, H]];
            const kWorld = Math.min(1, W / (x1 - x0), H / (y1 - y0));
            worldRef.current = d3.zoomIdentity.translate(W / 2 - kWorld * (x0 + x1) / 2, H / 2 - kWorld * (y0 + y1) / 2)
                .scale(kWorld);
            const zoom = d3.zoom<SVGSVGElement, unknown>().scaleExtent([kWorld, 12])
                .extent([[0, 0], [W, H]]).translateExtent([[x0, y0], [x1, y1]])
                .on('zoom', e => {
                    view.attr('transform', e.transform.toString());
                    view.selectAll<SVGCircleElement, Bubble>('circle')
                        .attr('r', b => Math.max(4, radius(b.spend)) / e.transform.k);
                    setHover(null);
                });
            root.call(zoom);
            zoomRef.current = zoom;
        });
        return () => {
            cancelled = true;
        };
    }, [bubbles, H, selectable]);

    // Highlight the selection without redrawing (which would reset the zoom)
    const selectedKey = (selected ?? []).join('|');
    useEffect(() => {
        if (!svgRef.current) return;
        const keys = selectedKey ? selectedKey.split('|') : [];
        d3.select(svgRef.current).selectAll<SVGCircleElement, Bubble>('circle')
            .attr('fill-opacity', b => bubbleOpacity(b.key, keys).fill)
            .attr('stroke-opacity', b => bubbleOpacity(b.key, keys).stroke);
    }, [selectedKey, bubbles]);

    if (bubbles.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{py: 6, textAlign: 'center'}}>
                {emptyText ?? 'No supplier countries in this dataset.'}
            </Typography>
        );
    }
    const zoomTo = (to: ZoomAction) => {
        if (!svgRef.current || !zoomRef.current) return;
        const t = d3.select(svgRef.current).transition().duration(typeof to === 'number' ? 200 : 500);
        if (typeof to === 'number') t.call(zoomRef.current.scaleBy, to);
        else t.call(zoomRef.current.transform, to === 'world' ? worldRef.current : d3.zoomIdentity);
    };

    return (
        <Box ref={boxRef} sx={{position: 'relative', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden',
            bgcolor: '#FFFFFF', ...(fill ? {height: '100%', boxSizing: 'border-box'} : {})}}>
            <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" role="img"
                 aria-label={`Spend by supplier country: ${bubbles.map(b => `${b.label} ${money(b.spend)}`).join(', ')}`}
                 style={{display: 'block', cursor: 'grab', touchAction: 'none'}}/>
            {hover && (
                <Paper elevation={3} sx={{position: 'absolute', left: hover.x + 12, top: hover.y + 12, px: 1, py: 0.5,
                    pointerEvents: 'none'}}>
                    <Typography variant="caption" sx={{fontWeight: 600}}>{hover.b.label}</Typography>
                    <Typography variant="caption" sx={{display: 'block'}}>{money(hover.b.spend)}</Typography>
                </Paper>
            )}
            <Typography variant="caption" color="text.secondary"
                        sx={{position: 'absolute', left: 12, bottom: 10, bgcolor: 'rgba(255,255,255,0.85)', px: 0.75, borderRadius: 1}}>
                Scroll to zoom · drag to pan{selectable ? ' · click to filter' : ''}
            </Typography>
            <Stack sx={{position: 'absolute', right: 10, bottom: 10, gap: 0.75}}>
                {BUTTONS.map(({label, Icon, action}) => (
                    <IconButton key={label} size="small" aria-label={label} title={label} onClick={() => zoomTo(action)}
                                sx={{bgcolor: '#FFFFFF', border: '1px solid', borderColor: 'divider', width: 28, height: 28,
                                    '&:hover': {bgcolor: '#F4F6F8'}}}>
                        <Icon sx={{fontSize: 16}}/>
                    </IconButton>
                ))}
            </Stack>
        </Box>
    );
};
